import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-8222">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-8223">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-8224">
              <span className="text-xs font-bold text-indigo-800" data-id="element-8225">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-8226">
            <div className="text-center" data-id="element-8227">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-8228">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-8229">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-8230">→</div>
            <div className="text-center" data-id="element-8231">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-8232">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-8233">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-8234">
            <table className="w-full text-sm" data-id="element-8235">
              <thead data-id="element-8236">
                <tr className="bg-indigo-100/70" data-id="element-8237">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-8238">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-8239">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-8240">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-8241">
                      <td className="px-3 py-1.5" data-id="element-8242">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-8243">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-8244">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-8245">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-8246">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-8247">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-8248">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-8249">
            <span className="text-2xl" data-id="element-8250">📝</span>
            <div className="text-left" data-id="element-8251">
              <div className="text-sm font-bold text-indigo-900" data-id="element-8252">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-8253">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-8254">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-8255">
            <div className="text-center" data-id="element-8256">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-8257">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-8258">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-8259">→</div>
            <div className="text-center" data-id="element-8260">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-8261">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-8262">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-8263">
          <table className="w-full" data-id="element-8264">
            <thead data-id="element-8265">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-8266">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-8267">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-8268">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-8269">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-8270">
                    <td className="px-4 py-3" data-id="element-8271">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-8272">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-8273">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-8274">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-8275">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-8276">
            <div className="flex items-start gap-3" data-id="element-8277">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-8278">
                <span className="text-blue-600 text-lg" data-id="element-8279">🔍</span>
              </div>
              <div data-id="element-8280">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-8281">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-8282">
                  De <strong data-id="element-8283">stam</strong> krijg je door <strong data-id="element-8284">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-8285">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-8286">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-8287">
              <div className="flex items-start gap-3" data-id="element-8288">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-8289">
                  <span className="text-emerald-600 text-lg" data-id="element-8290">📋</span>
                </div>
                <div data-id="element-8291">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-8292">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-8293">
                    <p data-id="element-8294">
                      <strong data-id="element-8295">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-8296">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-8297">
                      <strong data-id="element-8298">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-8299">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-8300">
                      <strong data-id="element-8301">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-8302">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-8303">
                      <strong data-id="element-8304">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-8305">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-8306">
            <div className="flex items-start gap-3" data-id="element-8307">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-8308">
                <span className="text-amber-600 text-lg" data-id="element-8309">💡</span>
              </div>
              <div data-id="element-8310">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-8311">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-8312">
                  Bij <strong data-id="element-8313">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-8314">jij loopt</em> wordt{' '}
                  <em data-id="element-8315">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}